import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PhoneOutgoing, Plus, Play, AlertCircle, Pause, CheckCircle2, Loader2, Code2, Search, Ban, Upload, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTenant } from '../components/TenantContext.jsx';
import { tenantsApi, avatarsApi, outboundApi } from '../api/tenants.js';
import { campaignApi } from '../api/outboundCampaign.js';
import { api } from '../api/client.js';
import { PageHeader, SectionHeader } from '../components/ui/PageHeader.jsx';
import { Card, Empty, DirectionChip } from '../components/ui/Card.jsx';
import { QuotaModal } from '../components/MinuteQuota.jsx';
import { Button } from '../components/ui/Button.jsx';
import { Input } from '../components/ui/Input.jsx';
import { CreateCampaignWizard } from '../components/Outbound/CreateCampaignWizard.jsx';
import { BlacklistTab } from '../components/Outbound/BlacklistTab.jsx';

// 活動列表一次抓多少（後端上限 500）— 大租戶累積數百個活動，預設 100 會列不完
const CAMPAIGN_FETCH_LIMIT = 500;
const CAMPAIGNS_PER_PAGE = 12;

// ─── isInDialWindow (test-slice-start) ─────────────────────────────────────
// 活動「已啟動」(status=running) 不等於「現在正在撥」：排程器出了 windowConfig 時段只是
// 跳過不撥（outboundScheduler.js:596 的 `continue`），status 從頭到尾維持 running
// → badge 整夜顯示「撥打中」。0910 顯榮實案：17:00 後實際撥出 0 通、log 每 30 秒印
// `skip ipcc/47eq…: hour=17 >= endHour=17`，客戶仍看到「撥打中」而以為系統沒停、手動按了暫停。
//
// 這裡不改 status（改成 paused 會讓排程器的 where status in ['running','scheduled'] 撈不到，
// 隔天 09:00 就不會自動接續了），只用卡片本來就有的 windowConfig 修正顯示。
// 規則與後端 isInWindowDetail 一致，含 ISO 週日 7→0 正規化（早期 API 寫入的 doc 會是 7）。
function isInDialWindow(w, now = new Date()) {
    if (!w) return true;
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: w.timezone || 'Asia/Taipei', weekday: 'short', hour: '2-digit', hour12: false,
    }).formatToParts(now);
    const DOW = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    const dow = DOW[parts.find((p) => p.type === 'weekday')?.value] ?? 1;
    const hour = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10) % 24;
    const days = Array.isArray(w.daysOfWeek)
        ? [...new Set(w.daysOfWeek.map(Number).filter(Number.isInteger).map((d) => (d === 7 ? 0 : d)))]
        : [1, 2, 3, 4, 5];
    if (!days.includes(dow)) return false;
    return hour >= (w.startHour ?? 9) && hour < (w.endHour ?? 21);
}
// ─── isInDialWindow (test-slice-end) ───────────────────────────────────────

export default function OutboundPage() {
    const { t } = useTranslation('outbound');
    const { hostId, isAdmin, canOperate } = useTenant();
    const [tenant, setTenant] = useState(null);
    const [quotaMsg, setQuotaMsg] = useState(null);   // 建立活動時分鐘數已用完 → 站內視窗（取代原生 alert）
    const [avatars, setAvatars] = useState([]);
    const [avatarId, setAvatarId] = useState('');
    const [campaigns, setCampaigns] = useState([]);
    const [search, setSearch] = useState('');              // 活動名稱 / id 搜尋
    const [statusFilter, setStatusFilter] = useState('');  // '' = 全部狀態
    const [page, setPage] = useState(1);                   // 活動列表頁碼（1-based）
    const [tab, setTab] = useState('quick');   // 'quick' = 快速外播（預設，在前）| 'campaigns' = 外播活動
    // viewer 唯讀：沒有快速外播 tab → 落在外播活動（角色是非同步載入的，所以用 effect 不寫死初始值）
    useEffect(() => { if (!canOperate && tab === 'quick') setTab('campaigns'); }, [canOperate, tab]);
    const [wizardOpen, setWizardOpen] = useState(false);
    // 禁撥名單分頁的兩顆頁首按鈕：只把「動作」暴露上來，state 仍留在 BlacklistTab（見該檔頭）
    const blacklistRef = useRef(null);
    const [concurrency, setConcurrency] = useState(0);     // 0 = use max
    const [items, setItems] = useState([]);                // [{ phone, name, vars }]
    const [phonesText, setPhonesText] = useState('');      // 快速外撥：一行一個電話
    const [job, setJob] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const pollRef = useRef(null);

    // SIP Trunk 模式沒有分機，併發上限 = trunk.channels（一通道=一席）；分機模式 = 分機數
    const maxConcurrency = tenant?.pbx?.mode === 'trunk'
        ? Math.max(1, Number(tenant?.trunk?.channels) || 1)
        : ((tenant?.ipPhones?.length || 0) || 1);

    useEffect(() => {
        if (!hostId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const [t, a, c] = await Promise.all([
                    tenantsApi.get(hostId),
                    avatarsApi.listForTenant(hostId).catch(() => ({ avatars: [] })),
                    campaignApi.list(hostId, { limit: CAMPAIGN_FETCH_LIMIT }).catch(() => ({ campaigns: [] })),
                ]);
                if (cancelled) return;
                setTenant(t.tenant);
                setAvatars(a.avatars || []);
                setCampaigns(c.campaigns || []);
                if ((a.avatars || []).length === 1) setAvatarId(a.avatars[0].id);
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [hostId]);

    const reloadCampaigns = async () => {
        const r = await campaignApi.list(hostId, { limit: CAMPAIGN_FETCH_LIMIT }).catch(() => ({ campaigns: [] }));
        setCampaigns(r.campaigns || []);
    };

    // 搜尋 + 狀態篩選 → 分頁
    const filteredCampaigns = useMemo(() => {
        const q = search.trim().toLowerCase();
        return campaigns.filter((c) => {
            if (statusFilter && c.status !== statusFilter) return false;
            if (!q) return true;
            return (c.name || '').toLowerCase().includes(q) || (c.id || '').toLowerCase().includes(q);
        });
    }, [campaigns, search, statusFilter]);

    const pageCount = Math.max(1, Math.ceil(filteredCampaigns.length / CAMPAIGNS_PER_PAGE));
    // 篩選後頁數變少（或剛換 tenant）→ 夾回合法範圍，不然會停在空白頁
    const safePage = Math.min(page, pageCount);
    useEffect(() => { if (page !== safePage) setPage(safePage); }, [page, safePage]);
    const pagedCampaigns = filteredCampaigns.slice((safePage - 1) * CAMPAIGNS_PER_PAGE, safePage * CAMPAIGNS_PER_PAGE);

    // 換 tenant / 改條件 → 回第一頁
    useEffect(() => { setPage(1); }, [hostId, search, statusFilter]);

    // 進行中或 paused 的 campaign → 30s poll 一次更新 stats
    useEffect(() => {
        const hasActive = campaigns.some(c => ['running', 'paused', 'scheduled'].includes(c.status));
        if (!hasActive) return;
        const t = setInterval(reloadCampaigns, 30000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaigns.map(c => c.status).join(',')]);

    async function start() {
        setError(null);
        if (!avatarId) return setError(new Error(t('err.pickAgent')));
        if (items.length === 0 || !items[0]?.phone) return setError(new Error(t('err.enterPhone')));
        try {
            const r = await outboundApi.start(hostId, {
                avatarId,
                items,
                concurrency: concurrency || undefined,
            });
            const jobId = r.jobId;
            const initial = { id: jobId, total: r.total, concurrency: r.concurrency, items: items.map((it) => ({ ...it, status: 'queued' })) };
            setJob(initial);
            pollJob(jobId);
        } catch (e) {
            setError(e);
        }
    }

    function pollJob(jobId) {
        clearInterval(pollRef.current);
        const tick = async () => {
            try {
                const r = await outboundApi.status(hostId, jobId);
                setJob(r.job);
                if (r.job.finishedAt) clearInterval(pollRef.current);
            } catch {
                // sidecar 暫時不通就繼續
            }
        };
        tick();
        pollRef.current = setInterval(tick, 1500);
    }
    useEffect(() => () => clearInterval(pollRef.current), []);

    if (loading) return <div className="text-ink-400 text-sm">{t('common:state.loading')}</div>;

    return (
        <div className="max-w-4xl">
            <PageHeader
                eyebrow={hostId ? `tenant · ${hostId}` : 'tenant'}
                title={t('page.title')}
                description={t('page.desc')}
                actions={
                    !canOperate ? null : tab === 'campaigns' ? (
                        <Button variant="accent" onClick={() => setWizardOpen(true)} disabled={!hostId}>
                            <Plus size={14} /> {t('action.newCampaign')}
                        </Button>
                    ) : tab === 'blacklist' ? (
                        <div className="flex gap-2">
                            <Button onClick={() => blacklistRef.current?.openAdd()} disabled={!hostId}>
                                <Plus size={14} /> {t('bl.addPhone')}
                            </Button>
                            {/* 上傳＝累加（0922 起，原整份取代）。只加不刪 → 風險等同逐筆新增，子帳號也開放。
                                批次刪除 / 清空仍限母帳號（BlacklistTab 內的 isAdmin 不動）。 */}
                            <Button variant="accent" disabled={!hostId} onClick={() => blacklistRef.current?.openImport()}>
                                <Upload size={14} /> {t('bl.importCsv')}
                            </Button>
                        </div>
                    ) : null
                }
            />

            {/* Tabs：外播活動 / 快速外播 */}
            <div className="flex items-center gap-1 mb-6 border-b border-cream-200">
                {[
                    canOperate && { id: 'quick', label: t('tab.quick'), icon: Play },
                    { id: 'campaigns', label: t('tab.campaigns'), icon: PhoneOutgoing },
                    { id: 'blacklist', label: t('tab.blacklist'), icon: Ban },
                ].filter(Boolean).map(({ id, label, icon: Icon }) => (
                    <button
                        key={id}
                        onClick={() => setTab(id)}
                        className={[
                            'inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition',
                            tab === id
                                ? 'border-accent-500 text-ink-900'
                                : 'border-transparent text-ink-400 hover:text-ink-700'
                        ].join(' ')}
                    >
                        <Icon size={14} /> {label}
                    </button>
                ))}
            </div>

            <CreateCampaignWizard
                open={wizardOpen}
                onClose={() => setWizardOpen(false)}
                hostId={hostId}
                onCreated={(_c, { quotaExhausted } = {}) => { setWizardOpen(false); reloadCampaigns(); if (quotaExhausted) setQuotaMsg(t('quotaNotice')); }}
            />
            {quotaMsg && <QuotaModal text={quotaMsg} onClose={() => setQuotaMsg(null)} />}

            {/* ───── Tab 1：外播活動（Firestore-backed campaigns）───── */}
            {tab === 'campaigns' && (
                campaigns.length === 0 ? (
                    <Empty title={t('section.emptyTitle')} description={t('section.emptyDesc')} />
                ) : (
                    <>
                        {/* 搜尋 + 狀態篩選 */}
                        <div className="flex flex-wrap items-center gap-2 mb-4">
                            <div className="relative flex-1 min-w-[200px]">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
                                <input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder={t('list.searchPh')}
                                    className="w-full pl-9 pr-3 py-2 bg-white border border-cream-300 rounded-md text-sm focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/15"
                                />
                            </div>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="px-3 py-2 bg-white border border-cream-300 rounded-md text-sm focus:outline-none focus:border-accent-500"
                            >
                                <option value="">{t('detail.filter.all')}</option>
                                {['draft', 'scheduled', 'running', 'paused', 'done'].map((s) => (
                                    <option key={s} value={s}>{t(`campaignStatus.${s}`)}</option>
                                ))}
                            </select>
                            <span className="mono-caption text-ink-400 shrink-0">
                                {t('section.campaignCount', { n: filteredCampaigns.length })}
                            </span>
                        </div>

                        {filteredCampaigns.length === 0 ? (
                            <div className="py-10 text-center text-sm text-ink-400">{t('list.emptyFiltered')}</div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {pagedCampaigns.map(c => {
                                    // 分類:campaign 自帶 campaignType 優先;否則反查 avatar.newMode
                                    const av = avatars.find(a => (a.avatarId || a.id) === c.avatarId);
                                    const isSurvey = c.campaignType === 'survey'
                                        || (!c.campaignType && (av?.newMode === 'survey' || av?.surveyMode === true));
                                    return <CampaignCard key={c.id} campaign={c} hostId={hostId} isSurvey={isSurvey} canOperate={canOperate} onChange={reloadCampaigns} />;
                                })}
                            </div>
                        )}

                        {pageCount > 1 && (
                            <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-cream-200 text-xs">
                                <div className="text-ink-400 tabular-nums">
                                    {(safePage - 1) * CAMPAIGNS_PER_PAGE + 1}–{Math.min(safePage * CAMPAIGNS_PER_PAGE, filteredCampaigns.length)} / {filteredCampaigns.length}
                                </div>
                                <div className="flex items-center gap-1">
                                    <button disabled={safePage <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}
                                        className="px-2 py-1 rounded border border-cream-300 bg-white disabled:opacity-40 hover:bg-cream-100">‹</button>
                                    {buildPageWindow(safePage, pageCount).map((p, i) => p === '…' ? (
                                        <span key={`e${i}`} className="px-1 text-ink-400">…</span>
                                    ) : (
                                        <button key={p} onClick={() => setPage(p)}
                                            className={`min-w-[28px] px-2 py-1 rounded border tabular-nums ${p === safePage ? 'border-accent-500 bg-accent-500 text-white' : 'border-cream-300 bg-white hover:bg-cream-100 text-ink-700'}`}>{p}</button>
                                    ))}
                                    <button disabled={safePage >= pageCount} onClick={() => setPage(p => Math.min(pageCount, p + 1))}
                                        className="px-2 py-1 rounded border border-cream-300 bg-white disabled:opacity-40 hover:bg-cream-100">›</button>
                                </div>
                            </div>
                        )}
                    </>
                )
            )}

            {/* ───── Tab 2：快速外播 ───── */}
            {tab === 'quick' && canOperate && (
              <>
                <SectionHeader title={t('quickDial.title')} description={t('quickDial.desc')} />

                {error && (
                    <div className="mb-4 px-4 py-3 bg-danger-100/60 border border-danger-500/30 text-danger-500 text-sm rounded-md flex items-center gap-3">
                        <AlertCircle size={16} />
                        {error.message}
                    </div>
                )}

                <div className="space-y-6">
                {/* 1. AI 角色 */}
                <Card>
                    <SectionHeader title={t('quick.step1.title')} description={t('quick.step1.desc')} />
                    {avatars.length === 0 ? (
                        <div className="text-sm text-ink-400 italic">
                            {t('quick.step1.noAvatar')}
                        </div>
                    ) : (
                        <select
                            value={avatarId}
                            onChange={(e) => setAvatarId(e.target.value)}
                            className="w-full max-w-md px-3 py-2 bg-white border border-cream-300 rounded-md text-sm focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/15"
                        >
                            <option value="">{t('select.pickOne')}</option>
                            {avatars.map((a) => (
                                <option key={a.id} value={a.id}>{a.name || a.id.slice(0, 8)}</option>
                            ))}
                        </select>
                    )}
                </Card>

                {/* 2. 並發數 */}
                <Card>
                    <SectionHeader
                        title={t('quick.step2.title')}
                        description={t('quick.step2.desc', { n: maxConcurrency })}
                    />
                    <div className="flex items-center gap-3">
                        <input
                            type="number"
                            min={1}
                            max={maxConcurrency}
                            value={concurrency || maxConcurrency}
                            onChange={(e) => setConcurrency(Number(e.target.value) || 0)}
                            className="w-24 px-3 py-2 bg-white border border-cream-300 rounded-md font-mono text-sm focus:outline-none focus:border-accent-500"
                        />
                        <span className="text-xs text-ink-400">{t('quick.step2.unit')}</span>
                    </div>
                </Card>

                {/* 3. 要撥的電話（快速，一行一個） */}
                <Card>
                    <SectionHeader title={t('quick.step3.title')} description={t('quick.step3.desc')} />
                    <textarea
                        value={phonesText}
                        onChange={(e) => {
                            const raw = e.target.value;
                            setPhonesText(raw);
                            // 逗號後的內容（舊版「電話,姓名」）忽略 — 開場白統一在活動裡客製
                            const parsed = raw.split('\n').map((line) => {
                                const phone = line.split(',')[0].replace(/[^\d+#]/g, '').trim();
                                return phone ? { phone, name: '', vars: {} } : null;
                            }).filter(Boolean);
                            setItems(parsed);
                        }}
                        placeholder={'0912345678\n0987654321\n0922333444'}
                        rows={4}
                        className="w-full px-3 py-2 bg-white border border-cream-300 rounded-md font-mono text-sm focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/15"
                    />
                    {items.length > 0 && (
                        <div className="mt-2 text-xs text-ink-500">{t('quick.itemsCount', { n: items.length })}</div>
                    )}
                </Card>

                {/* 5. 撥打 */}
                <div className="flex justify-end">
                    <Button
                        variant="accent"
                        onClick={start}
                        disabled={!avatarId || items.length === 0 || (job && !job.finishedAt)}
                    >
                        <Play size={14} /> {items.length > 1 ? t('quick.dialNowN', { n: items.length }) : t('quick.dialNow')}
                    </Button>
                </div>

                {/* Job 狀態 */}
                {job && (
                    <Card>
                        <SectionHeader title={t('quick.job.title')} description={job.finishedAt ? t('quick.job.done', { sec: Math.round((job.finishedAt - job.startedAt) / 1000) }) : t('quick.job.dialing', { n: job.concurrency })} />
                        {/* 六格（含「等線路」）—— 少了它 queued+calling+answered+failed 會加不到 total，
                            線路滿在等重試的那幾筆看起來像掉件 */}
                        <div className="grid grid-cols-6 gap-3 mb-4">
                            <Stat label={t('quick.stat.total')} value={job.total} color="ink-700" />
                            <Stat label={t('quick.stat.queued')} value={job.queued || 0} color="ink-400" />
                            <Stat label={t('quick.stat.calling')} value={job.calling || 0} color="warn-500" />
                            <Stat label={t('quick.stat.waitingLine', '等線路')} value={job.waiting_line || 0} color="warn-500" />
                            <Stat label={t('quick.stat.answered')} value={job.answered || 0} color="success-500" />
                            <Stat label={t('quick.stat.failed')} value={job.failed || 0} color="danger-500" />
                        </div>
                        <div className="border border-cream-200 rounded-md max-h-72 overflow-y-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-cream-100 sticky top-0">
                                    <tr className="text-[10px] uppercase tracking-wider text-ink-400 font-mono">
                                        <th className="text-left px-3 py-2 font-medium w-12">#</th>
                                        <th className="text-left px-3 py-2 font-medium">{t('quick.col.phone')}</th>
                                        <th className="text-left px-3 py-2 font-medium">{t('quick.col.name')}</th>
                                        <th className="text-left px-3 py-2 font-medium">{t('quick.col.status')}</th>
                                        <th className="text-left px-3 py-2 font-medium">{t('quick.col.note')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(job.items || []).map((it, i) => (
                                        <tr key={i} className="border-t border-cream-100">
                                            <td className="px-3 py-1.5 text-ink-400 mono-caption">{i + 1}</td>
                                            <td className="px-3 py-1.5 font-mono">{it.phone}</td>
                                            <td className="px-3 py-1.5">{it.name || '—'}</td>
                                            <td className="px-3 py-1.5">
                                                <StatusBadge status={it.status} />
                                            </td>
                                            <td className="px-3 py-1.5 text-xs text-ink-500 truncate max-w-md">
                                                {it.error || (it.sessionId ? `sid ${it.sessionId.slice(0, 8)}` : '')}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                )}
                </div>
              </>
            )}

            {/* ───── Tab 3：禁撥名單（撥出前過濾，per tenant） ───── */}
            {tab === 'blacklist' && <BlacklistTab ref={blacklistRef} hostId={hostId} isAdmin={isAdmin} canOperate={canOperate} />}
        </div>
    );
}

// 頁碼窗：頁數多時只顯示 1 … 前後 2 頁 … 最後一頁
function buildPageWindow(current, count) {
    if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1);
    const out = [1];
    const lo = Math.max(2, current - 2);
    const hi = Math.min(count - 1, current + 2);
    if (lo > 2) out.push('…');
    for (let p = lo; p <= hi; p++) out.push(p);
    if (hi < count - 1) out.push('…');
    out.push(count);
    return out;
}

function Stat({ label, value, color }) {
    return (
        <div className="bg-cream-100 rounded-md px-3 py-2 text-center">
            <div className="mono-caption">{label}</div>
            <div className={`text-2xl font-mono tnum text-${color}`}>{value}</div>
        </div>
    );
}

function StatusBadge({ status }) {
    const { t } = useTranslation('outbound');
    const map = {
        queued: { c: 'bg-cream-200 text-ink-500', t: t('jobStatus.queued') },
        calling: { c: 'bg-warn-100 text-warn-500', t: t('jobStatus.calling') },
        answered: { c: 'bg-success-100 text-success-500', t: t('jobStatus.answered') },
        failed: { c: 'bg-danger-100/60 text-danger-500', t: t('jobStatus.failed') },
        completed: { c: 'bg-success-100 text-success-500', t: t('jobStatus.completed') },
    };
    const s = map[status] || map.queued;
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.c}`}>
            {s.t}
        </span>
    );
}

function CampaignCard({ campaign: c, hostId, isSurvey = false, canOperate = true, onChange }) {
    const { t } = useTranslation('outbound');
    const [quotaMsg, setQuotaMsg] = useState(null);   // 分鐘數用完的提示視窗（放在 Link 外，點視窗不會開活動頁）
    const stats = c.stats || {};
    const total = stats.totalContacts || 0;
    const done = stats.doneSuccess || 0;
    const failed = stats.doneFailed || 0;
    const pending = stats.pending || 0;
    const inProgress = stats.inProgress || 0;
    const retryPending = stats.retryPending || 0;
    const progressPct = total > 0 ? Math.round(((done + failed) / total) * 100) : 0;

    // 名單匯入中（claude 解析）狀態 poll
    const [importJob, setImportJob] = useState(null);
    useEffect(() => {
        if (!c.importJobId || total > 0) return;   // 已有 contacts 就不用 poll 了
        let cancelled = false;
        const poll = async () => {
            try {
                const r = await api('GET', `/ivr-builder/api/outbound-import/jobs/${c.importJobId}`);
                if (cancelled) return;
                setImportJob(r.job);
                if (['done', 'error'].includes(r.job.status)) onChange?.();
            } catch {}
        };
        poll();
        const t = setInterval(poll, 3000);
        return () => { cancelled = true; clearInterval(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [c.importJobId, total]);

    const statusMap = {
        draft: { c: 'bg-cream-200 text-ink-500', t: t('campaignStatus.draft') },
        scheduled: { c: 'bg-accent-50 text-accent-700', t: t('campaignStatus.scheduled') },
        running: { c: 'bg-success-100 text-success-500', t: t('campaignStatus.running'), icon: Loader2 },
        paused: { c: 'bg-warn-100 text-warn-500', t: t('campaignStatus.paused'), icon: Pause },
        done: { c: 'bg-cream-200 text-ink-500', t: t('campaignStatus.done'), icon: CheckCircle2 },
        archived: { c: 'bg-cream-100 text-ink-300', t: t('campaignStatus.archived') },
    };
    // running 但不在撥打時段 → 顯示「非撥打時段」而不是「撥打中」（status 不動，隔天自動接續）
    const offWindow = c.status === 'running' && !isInDialWindow(c.windowConfig);
    // 排程因分鐘數用盡而暫停 → 跟手動暫停分開顯示：要客戶去充值、再按恢復
    const quotaPaused = c.status === 'paused' && c.pauseReason === 'quota_exhausted';
    const s = quotaPaused
        ? { c: 'bg-danger-100/60 text-danger-500', t: t('campaignStatus.quotaPaused'), icon: Pause }
        : offWindow
        ? { c: 'bg-cream-200 text-ink-500', t: t('campaignStatus.offWindow'), icon: Clock }
        : (statusMap[c.status] || statusMap.draft);
    const Icon = s.icon;

    const toggle = async (e) => {
        e.preventDefault();
        try {
            let r = null;
            if (c.status === 'running') await campaignApi.pause(hostId, c.id);
            else if (c.status === 'paused') await campaignApi.resume(hostId, c.id);
            else if (c.status === 'draft') r = await campaignApi.start(hostId, c.id);
            if (r?.quotaExhausted) setQuotaMsg(t('quotaNotice'));   // 啟動時分鐘數用完：活動照開，排程會先暫停
            onChange?.();
        } catch (err) {
            if (err.code === 'quota_exhausted') setQuotaMsg(t('quotaResumeBlocked'));
            else alert(t('card.toggleFail', { msg: err.message }));
        }
    };

    return (
        <>
        <Link to={`/outbound/${c.id}`} className="block">
            <Card className={`transition cursor-pointer ${isSurvey ? 'hover:border-purple-300 border-l-[3px] border-l-purple-400' : 'hover:border-accent-300'}`}>
                <div className="flex items-start justify-between mb-2">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${isSurvey ? 'bg-purple-100 text-purple-700' : 'bg-accent-50 text-accent-700'}`}>
                                {isSurvey ? t('card.survey') : t('card.ivr')}
                            </span>
                            {c.isApiTemplate && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 bg-sky-100 text-sky-700" title={t('card.apiTemplateTip')}>
                                    <Code2 size={10} /> {t('card.apiTemplate')}
                                </span>
                            )}
                            <div className="heading-display text-base font-semibold text-ink-900 truncate">{c.name}</div>
                        </div>
                        <div className="font-mono text-[10px] text-ink-400 mt-0.5">{c.id.slice(0, 12)}…</div>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${s.c}`}>
                        {Icon && <Icon size={10} className={c.status === 'running' && !offWindow ? 'animate-spin' : ''} />}
                        {s.t}
                    </span>
                </div>

                <div className="text-[11px] text-ink-500 mb-2 flex items-center gap-3">
                    <span>{t('card.contactsN', { n: total })}</span>
                    <span>·</span>
                    <span>{c.windowConfig?.startHour ?? 9}:00-{c.windowConfig?.endHour ?? 21}:00</span>
                    <span>·</span>
                    <span>{t('card.concurrencyN', { n: (c.endpoints?.length || c.concurrency || 2) })}</span>
                </div>

                {/* 名單匯入中（claude 解析 / 寫入）狀態 */}
                {importJob && total === 0 && ['parsing', 'committing'].includes(importJob.status) && (
                    <>
                        <div className="h-1.5 bg-cream-200 rounded-full overflow-hidden mb-1.5">
                            <div className="h-full bg-warn-500 transition-all animate-pulse" style={{ width: `${importJob.progress || 5}%` }} />
                        </div>
                        <div className="text-[11px] text-warn-500 flex items-center gap-1.5">
                            <Loader2 size={10} className="animate-spin" />
                            {importJob.status === 'parsing' ? t('card.parsing') : t('card.writing')}
                            <span className="ml-auto font-mono">{importJob.progress || 0}%</span>
                        </div>
                    </>
                )}

                {/* 撥打進度 bar（有 contacts 才顯示） */}
                {total > 0 && (
                    <>
                        <div className="h-1.5 bg-cream-200 rounded-full overflow-hidden mb-2">
                            <div className="h-full bg-accent-500 transition-all" style={{ width: `${progressPct}%` }} />
                        </div>

                        <div className="text-[11px] text-ink-500 flex items-center gap-2 tabular-nums">
                            <span className="text-success-500" title={isSurvey ? t('card.tipDoneVisit') : t('card.tipDialSuccess')}>✓ {done}{isSurvey ? t('card.doneVisit') : ''}</span>
                            <span>·</span>
                            <span className="text-warn-500">⟳ {retryPending}</span>
                            <span>·</span>
                            <span className="text-accent-700">{t('card.pendingN', { n: inProgress + pending })}</span>
                            {failed > 0 && <><span>·</span><span className="text-danger-500" title={isSurvey ? t('card.tipNotVisited') : t('card.tipDialFailed')}>✗ {failed}</span></>}
                            <span className="ml-auto font-mono">{progressPct}%</span>
                        </div>
                    </>
                )}

                {/* 沒 contacts 也沒 job → 提示需匯入 */}
                {total === 0 && !importJob && (
                    <div className="text-[11px] text-ink-400 italic">{c.isApiTemplate ? t('card.apiTemplateNote') : t('card.noList')}</div>
                )}

                {canOperate && ['running', 'paused', 'draft'].includes(c.status) && (
                    <button onClick={toggle}
                        className="mt-2 text-[11px] text-accent-700 hover:underline">
                        {c.status === 'running' ? t('card.pause') : c.status === 'paused' ? t('card.resume') : t('card.start')}
                    </button>
                )}
            </Card>
        </Link>
        {quotaMsg && <QuotaModal text={quotaMsg} onClose={() => setQuotaMsg(null)} />}
        </>
    );
}
